import { Link, useParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import WorkCard from '@/components/WorkCard';
import { categories, works } from '@/mocks/doujin';

const statusStyles: Record<string, string> = {
  连载中: 'bg-primary-100 text-primary-800',
  汉化完成: 'bg-secondary-100 text-secondary-900',
  即将开坑: 'bg-accent-100 text-accent-800',
};

export default function ArticlePage() {
  const { slug = '' } = useParams();
  const work = works.find((w) => w.slug === slug) || works[0];
  const category = categories.find((c) => c.slug === work.category);
  const related = works.filter((w) => w.slug !== work.slug && w.category === work.category).slice(0, 3);
  const fallbackRelated = related.length > 0 ? related : works.filter((w) => w.slug !== work.slug).slice(0, 3);

  useSeo({
    title: `${work.title}（${work.status}）最新章节 ${work.latestChapter} - 今日同人库`,
    description: `${work.summary} 今日同人库收录「${work.title}」的更新动态与汉化连载进度，最新章节：${work.latestChapter}。`,
    keywords: `今日同人库,${work.title},同人汉化,同人连载,${category?.name || ''}`,
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: work.title,
    description: work.summary,
    inLanguage: 'zh-CN',
    author: { '@type': 'Organization', name: work.author },
    dateModified: '2026-08-18',
    keywords: work.tags.join(','),
    image: work.cover,
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="px-4 md:px-6 max-w-7xl mx-auto py-8">
        <nav className="text-sm text-foreground-500 mb-6 flex flex-wrap items-center" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700 whitespace-nowrap">首页</Link>
          <span className="mx-2">/</span>
          <Link to={`/category/${category?.slug}`} className="hover:text-primary-700 whitespace-nowrap">
            {category?.name}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground-700 whitespace-nowrap truncate">{work.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg overflow-hidden border border-background-200">
              <img src={work.cover} alt={`${work.title}封面`} className="w-full aspect-[3/4] object-cover object-top" />
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusStyles[work.status]}`}>{work.status}</span>
              {work.tags.map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 text-xs">#{t}</span>
              ))}
            </div>
            <h1 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-foreground-950">{work.title}</h1>
            <p className="mt-3 text-foreground-600 text-sm md:text-base leading-relaxed">{work.summary}</p>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: '最近更新', value: work.updateTime },
                { label: '最新章节', value: work.latestChapter },
                { label: '已汉化', value: work.totalChapters > 0 ? `${work.totalChapters} 话` : '连载筹备中' },
                { label: '浏览热度', value: `${(work.views / 1000).toFixed(1)}k` },
              ].map((m) => (
                <div key={m.label} className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-400">{m.label}</p>
                  <p className="mt-1 text-sm font-semibold text-foreground-800 line-clamp-1">{m.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-background-100 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-500 text-background-50">
                  <i className="ri-pen-nib-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400">原作 / 汉化来源</p>
                  <p className="text-sm font-semibold text-foreground-800">{work.author}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-foreground-500 text-sm">
                <i className="ri-question-line text-primary-600"></i>
                <span className="hidden sm:inline">更新进度有疑问？</span>
                <Link to="/faq" className="text-primary-700 hover:text-primary-600 font-medium whitespace-nowrap">追更指南</Link>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">最新章节内容预览</h2>
              <div className="space-y-4">
                {work.content.map((p, i) => (
                  <p key={i} className="text-foreground-700 text-sm md:text-base leading-relaxed">{p}</p>
                ))}
              </div>
              <p className="mt-5 text-xs text-foreground-400 border-l-2 border-primary-300 pl-3">
                以上为最新章节内容节选。完整正文请以原作为准，本站仅提供更新动态与进度追踪。
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关作品推荐</h2>
            <Link to={`/category/${category?.slug}`} className="inline-flex items-center gap-1 text-primary-700 text-sm hover:text-primary-600 whitespace-nowrap">
              更多{category?.name} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {fallbackRelated.map((w) => (
              <WorkCard key={w.slug} work={w} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}